import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Avatar,
  Divider,
  IconButton,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import PaymentIcon from '@mui/icons-material/Payment';
import AssignmentIcon from '@mui/icons-material/Assignment';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { useDataProvider, useRefresh } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useBrand } from './components/ServerBrandProvider';
import { tenantService } from './services/tenantService';
import { PageTitle } from './components/PageTitle';
import { useTenant } from './contexts/TenantContext';
import { TaskDialog } from './components/TaskDialog';
import { useTenantTerminology } from './hooks/useTenantTerminology';
import { isGolfOSVariant } from './config/branding';

const MetricCard = ({ title, count, icon, color, onClick, subtitle, trend }: any) => {
  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': onClick ? {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
          transform: 'translateY(-2px)',
        } : {},
        transition: 'all 0.2s ease',
      }}
      onClick={onClick}
    >
      <CardContent sx={{ padding: '20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '8px',
              backgroundColor: `${color}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: '#2D3748' }}>
              {count}
            </Typography>
            <Typography color="textSecondary" variant="body2">
              {title}
            </Typography>
            {subtitle && (
              <Typography color="textSecondary" variant="caption">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Typography color="success.main" variant="caption" display="block">
                {trend}
              </Typography>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const SectionCard = ({ title, icon, color, children, actions }: any) => {
  return (
    <Paper sx={{ borderRadius: '16px', overflow: 'hidden', height: '100%' }}>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
          borderBottom: `1px solid ${color}20`,
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: `${color}20`, color: color, width: 40, height: 40 }}>
              {icon}
            </Avatar>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#2D3748' }}>
              {title}
            </Typography>
          </Box>
          {actions && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {actions}
            </Box>
          )}
        </Box>
      </Box>
      <CardContent sx={{ p: 3 }}>
        {children}
      </CardContent>
    </Paper>
  );
};

/**
 * GolfOS-only: Service & Field Dispatch with the two operations metrics (no CRM/Sales/Assets, no quick actions).
 */
const GolfOSDashboard = () => {
  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const { brand } = useBrand();
  const { t } = useTenantTerminology();
  const { currentTenant } = useTenant();
  const [loading, setLoading] = useState(true);
  const [taskCount, setTaskCount] = useState(0);
  const [workflowCount, setWorkflowCount] = useState(0);

  const primaryColor = brand?.colors?.primary || '#2D6A2F';
  const secondaryColor = brand?.colors?.secondary || '#8DC63F';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [tasksRes, workflowsRes] = await Promise.all([
          dataProvider
            .getList('tasks', {
              pagination: { page: 1, perPage: 1 },
              sort: { field: 'id', order: 'ASC' },
              filter: {},
            })
            .catch(() => ({ total: 0 })),
          dataProvider
            .getList('workflows', {
              pagination: { page: 1, perPage: 1 },
              sort: { field: 'id', order: 'ASC' },
              filter: {},
            })
            .catch(() => ({ total: 0 })),
        ]);
        setTaskCount(tasksRes.total || 0);
        setWorkflowCount(workflowsRes.total || 0);
      } catch (e) {
        console.error('GolfOS dashboard load failed:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dataProvider, currentTenant?.code]);

  return (
    <Box>
      <PageTitle
        title="Dashboard"
        subtitle={t('dashboard.golfos.subtitle', 'Service requests and field dispatch')}
        icon={<DashboardIcon />}
      />
      <Box sx={{ p: 3 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={10} lg={7}>
            <SectionCard
              title={t('dashboard.operations.title', 'Service & Field Dispatch')}
              icon={<AssignmentIcon />}
              color={secondaryColor}
              actions={[
                <Button
                  key="tasks"
                  size="small"
                  onClick={() => navigate('/tasks')}
                  endIcon={<ArrowForwardIcon />}
                >
                  {t('menu.tasks', 'Service Requests')}
                </Button>,
                <Button
                  key="workflows"
                  size="small"
                  onClick={() => navigate('/workflows')}
                  endIcon={<ArrowForwardIcon />}
                >
                  {t('pages.workflows', 'Workflows')}
                </Button>,
              ]}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <MetricCard
                    title={t('dashboard.totalTasks', 'Total Service Requests')}
                    count={loading ? '...' : taskCount}
                    icon={<AssignmentIcon />}
                    color={secondaryColor}
                    onClick={() => navigate('/tasks')}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MetricCard
                    title={t('pages.workflows', 'Workflows')}
                    count={loading ? '...' : workflowCount}
                    icon={<AssignmentIcon />}
                    color={primaryColor}
                    onClick={() => navigate('/workflows')}
                  />
                </Grid>
              </Grid>
            </SectionCard>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export const Dashboard = () => {
  if (isGolfOSVariant) {
    return <GolfOSDashboard />;
  }

  const dataProvider = useDataProvider();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const { brand } = useBrand();
  const { currentTenant } = useTenant();
  const { t } = useTenantTerminology();
  const [createTaskDialogOpen, setCreateTaskDialogOpen] = useState(false);

  // State for all metrics
  const [dashboardData, setDashboardData] = useState({
    crm: {
      customers: 0,
      leads: 0,
      opportunities: 0,
      recentLeads: [],
      conversionRate: 0,
    },
    sales: {
      estimates: 0,
      invoices: 0,
      payments: 0,
      totalRevenue: 0,
      pendingInvoices: 0,
      overdueInvoices: 0,
    },
    operations: {
      tasks: 0,
      workflows: 0,
      activeTasks: 0,
      completedTasks: 0,
      overdueTasks: 0,
      recentTasks: [],
    },
    assets: {
      drivers: 0,
      vehicles: 0,
      activeDrivers: 0,
      maintenanceAlerts: 0,
      reminders: { total: 0, overdue: 0, pending: 0 },
    }
  });
  const [loading, setLoading] = useState(true);

  // Brand colors with defaults
  const primaryColor = brand?.colors?.primary || '#1976d2';
  const secondaryColor = brand?.colors?.secondary || '#9c27b0';
  const successColor = '#2e7d32';
  const warningColor = '#ed6c02';
  const errorColor = '#d32f2f';

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch all data in parallel
        const [
          customersResponse,
          leadsResponse,
          opportunitiesResponse,
          estimatesResponse,
          invoicesResponse,
          paymentsResponse,
          tasksResponse,
          workflowsResponse,
          driversResponse,
          vehiclesResponse
        ] = await Promise.all([
          dataProvider.getList('customers', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('leads', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('opportunities', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('estimates', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('invoices', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('crm/payments', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('tasks', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('workflows', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('drivers', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
          dataProvider.getList('equipment', { pagination: { page: 1, perPage: 1 }, sort: { field: 'id', order: 'ASC' }, filter: {} }).catch(() => ({ total: 0 })),
        ]);

        // Fetch recent leads for CRM section
        const recentLeadsResponse = await dataProvider.getList('leads', {
          pagination: { page: 1, perPage: 5 },
          sort: { field: 'createdAt', order: 'DESC' },
          filter: {}
        }).catch(() => ({ data: [] }));

        // Fetch recent tasks for Operations section
        const recentTasksResponse = await dataProvider.getList('tasks', {
          pagination: { page: 1, perPage: 5 },
          sort: { field: 'createdAt', order: 'DESC' },
          filter: {}
        }).catch(() => ({ data: [] }));

        // Calculate conversion rate (opportunities / leads * 100)
        const conversionRate = leadsResponse.total > 0 ?
          Math.round((opportunitiesResponse.total / leadsResponse.total) * 100) : 0;

        setDashboardData({
          crm: {
            customers: customersResponse.total || 0,
            leads: leadsResponse.total || 0,
            opportunities: opportunitiesResponse.total || 0,
            recentLeads: recentLeadsResponse.data || [],
            conversionRate,
          },
          sales: {
            estimates: estimatesResponse.total || 0,
            invoices: invoicesResponse.total || 0,
            payments: paymentsResponse.total || 0,
            totalRevenue: 0, // Would need to calculate from payments
            pendingInvoices: 0, // Would need to filter by status
            overdueInvoices: 0, // Would need to filter by due date
          },
          operations: {
            tasks: tasksResponse.total || 0,
            workflows: workflowsResponse.total || 0,
            activeTasks: 0, // Would need to filter by status
            completedTasks: 0, // Would need to filter by status
            overdueTasks: 0, // Would need to filter by due date
            recentTasks: recentTasksResponse.data || [],
          },
          assets: {
            drivers: driversResponse.total || 0,
            vehicles: vehiclesResponse.total || 0,
            activeDrivers: 0, // Would need to filter by status
            maintenanceAlerts: 0, // Would need to check equipment maintenance
            reminders: { total: 0, overdue: 0, pending: 0 }, // Would fetch from reminders endpoint
          }
        });

      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [dataProvider, currentTenant?.code]); // Reload dashboard data when tenant changes

  const quickActions = [
    { label: t('dashboard.addCustomer', 'Add Customer'), icon: <AddIcon />, onClick: () => navigate('/customers/create'), color: primaryColor },
    { label: t('dashboard.addLead', 'Add Lead'), icon: <AddIcon />, onClick: () => navigate('/leads/create'), color: successColor },
    { label: 'Create Estimate', icon: <AddIcon />, onClick: () => navigate('/estimates/create'), color: warningColor },
    { label: t('dashboard.addTask', 'Add Task'), icon: <AddIcon />, onClick: () => setCreateTaskDialogOpen(true), color: secondaryColor },
  ];

  return (
    <Box>
      <PageTitle
        title="Dashboard"
        subtitle="Overview of your business operations"
        icon={<DashboardIcon />}
      />
      <Box sx={{ p: 3 }}>

        {/* Quick Actions */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Quick Actions
          </Typography>
          <Grid container spacing={2}>
            {quickActions.map((action, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={action.icon}
                  onClick={action.onClick}
                  sx={{
                    py: 1.5,
                    borderColor: `${action.color}40`,
                    color: action.color,
                    '&:hover': {
                      borderColor: action.color,
                      backgroundColor: `${action.color}10`,
                    }
                  }}
                >
                  {action.label}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Main Dashboard Sections */}
        <Grid container spacing={4}>

          {/* CRM Section */}
          <Grid item xs={12} lg={6}>
            <SectionCard
              title={t('dashboard.crm.title', 'Customer Relationship Management')}
              icon={<BusinessIcon />}
              color={primaryColor}
              actions={[
                <Button
                  key="view-crm"
                  size="small"
                  onClick={() => navigate('/customers')}
                  endIcon={<ArrowForwardIcon />}
                >
                  View All
                </Button>
              ]}
            >
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <MetricCard
                    title={t('dashboard.customers', 'Customers')}
                    count={loading ? '...' : dashboardData.crm.customers}
                    icon={<BusinessIcon />}
                    color={primaryColor}
                    onClick={() => navigate('/customers')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MetricCard
                    title={t('dashboard.leads', 'Leads')}
                    count={loading ? '...' : dashboardData.crm.leads}
                    icon={<PersonAddIcon />}
                    color={successColor}
                    onClick={() => navigate('/leads')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MetricCard
                    title={t('dashboard.opportunities', 'Opportunities')}
                    count={loading ? '...' : dashboardData.crm.opportunities}
                    subtitle={`${dashboardData.crm.conversionRate}% conversion`}
                    icon={<TrendingUpIcon />}
                    color={warningColor}
                    onClick={() => navigate('/opportunities')}
                  />
                </Grid>
              </Grid>

              {dashboardData.crm.recentLeads.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('dashboard.recentLeads', 'Recent Leads')}
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {dashboardData.crm.recentLeads.slice(0, 3).map((lead: any, index) => (
                      <ListItem
                        key={lead.id}
                        sx={{
                          px: 0,
                          py: 0.5,
                          cursor: 'pointer',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                        onClick={() => navigate(`/leads/${lead.id}/show`)}
                      >
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <PersonAddIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={lead.company || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Unnamed Lead'}
                          secondary={lead.email || lead.phone || 'No contact info'}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 1 }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </SectionCard>
          </Grid>

          {/* Sales Section */}
          <Grid item xs={12} lg={6}>
            <SectionCard
              title="Sales & Revenue"
              icon={<ReceiptIcon />}
              color={successColor}
              actions={[
                <Button
                  key="view-sales"
                  size="small"
                  onClick={() => navigate('/estimates')}
                  endIcon={<ArrowForwardIcon />}
                >
                  View All
                </Button>
              ]}
            >
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                  <MetricCard
                    title="Estimates"
                    count={loading ? '...' : dashboardData.sales.estimates}
                    icon={<RequestQuoteIcon />}
                    color={warningColor}
                    onClick={() => navigate('/estimates')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MetricCard
                    title="Invoices"
                    count={loading ? '...' : dashboardData.sales.invoices}
                    icon={<ReceiptIcon />}
                    color={successColor}
                    onClick={() => navigate('/invoices')}
                  />
                </Grid>
                <Grid item xs={4}>
                  <MetricCard
                    title="Payments"
                    count={loading ? '...' : dashboardData.sales.payments}
                    icon={<PaymentIcon />}
                    color={primaryColor}
                    onClick={() => navigate('/crm/payments')}
                  />
                </Grid>
              </Grid>

              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Track your sales pipeline and revenue
                </Typography>
              </Box>
            </SectionCard>
          </Grid>

          {/* Operations Section */}
          <Grid item xs={12} lg={6}>
            <SectionCard
              title={t('dashboard.operations.title', 'Operations & Tasks')}
              icon={<AssignmentIcon />}
              color={secondaryColor}
              actions={[
                <Button
                  key="view-tasks"
                  size="small"
                  onClick={() => navigate('/tasks')}
                  endIcon={<ArrowForwardIcon />}
                >
                  View All
                </Button>
              ]}
            >
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <MetricCard
                    title={t('dashboard.totalTasks', 'Total Tasks')}
                    count={loading ? '...' : dashboardData.operations.tasks}
                    icon={<AssignmentIcon />}
                    color={secondaryColor}
                    onClick={() => navigate('/tasks')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MetricCard
                    title="Workflows"
                    count={loading ? '...' : dashboardData.operations.workflows}
                    icon={<AssignmentIcon />}
                    color={primaryColor}
                    onClick={() => navigate('/workflows')}
                  />
                </Grid>
              </Grid>

              {dashboardData.operations.recentTasks.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('dashboard.recentTasks', 'Recent Tasks')}
                  </Typography>
                  <List sx={{ p: 0 }}>
                    {dashboardData.operations.recentTasks.slice(0, 3).map((task: any, index) => (
                      <ListItem
                        key={task.id}
                        sx={{
                          px: 0,
                          py: 0.5,
                          cursor: 'pointer',
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                        onClick={() => navigate(`/tasks/${task.id}/show`)}
                      >
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <AssignmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={task.title || task.name || 'Unnamed Task'}
                          secondary={task.description || task.notes || 'No description available'}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                        <ArrowForwardIcon sx={{ fontSize: 16, color: 'text.secondary', ml: 1 }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </SectionCard>
          </Grid>

          {/* Assets Section */}
          <Grid item xs={12} lg={6}>
            <SectionCard
              title={t('dashboard.assets.title', 'Assets & Resources')}
              icon={<LocalShippingIcon />}
              color={primaryColor}
              actions={[
                <Button
                  key="view-assets"
                  size="small"
                  onClick={() => navigate('/equipment')}
                  endIcon={<ArrowForwardIcon />}
                >
                  View All
                </Button>
              ]}
            >
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <MetricCard
                    title={t('dashboard.drivers', 'Drivers')}
                    count={loading ? '...' : dashboardData.assets.drivers}
                    icon={<PeopleIcon />}
                    color={primaryColor}
                    onClick={() => navigate('/drivers')}
                  />
                </Grid>
                <Grid item xs={6}>
                  <MetricCard
                    title={t('dashboard.vehicles', 'Vehicles')}
                    count={loading ? '...' : dashboardData.assets.vehicles}
                    icon={<LocalShippingIcon />}
                    color={secondaryColor}
                    onClick={() => navigate('/equipment')}
                  />
                </Grid>
              </Grid>

              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {t('dashboard.manageFleet', 'Manage your fleet and personnel')}
                </Typography>
              </Box>
            </SectionCard>
          </Grid>

        </Grid>
      </Box>


      {/* Task Dialog (Create) */}
      <TaskDialog
        open={createTaskDialogOpen}
        onClose={() => setCreateTaskDialogOpen(false)}
        onSuccess={() => {
          refresh();
        }}
      />
    </Box>
  );
};
